import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { getLead, addLeadActivity, updateLead } from "../../services/leadService";
import LeadActivityItem from "../LeadActivityItem/LeadActivityItem";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import LeadAssigneeSelect from "../LeadAssigneeSelect/LeadAssigneeSelect";
import { extractZodFieldError } from "../../utils/zodErrors";
import { memberDisplayName } from "../../utils/memberDisplay";
import styles from "./LeadDetailDrawer.module.css";

/**
 * LeadDetailDrawer — drawer lateral com detalhes do lead, steps de estágio,
 * responsável, histórico de atividades e ações (editar/converter/remover).
 *
 * Props:
 *   lead              {Object}                       — lead inicial (será re-fetched)
 *   stages            {Array}                        — config completa de stages
 *   stageLabels       {Record<string,string>}        — atalho stage → label
 *   members           {Array}                        — `TenantMember[]` da equipe (useTenantMembers)
 *   membersLoading    {boolean}                      — equipe carregando
 *   membersError      {boolean}                      — falha ao carregar a equipe (degrada sem quebrar o drawer)
 *   onClose           {() => void}
 *   onEdit            {(lead) => void}               — abre LeadFormModal em modo edição
 *   onConvert         {(lead) => Promise<void>}      — converter em cliente
 *   onDelete          {(lead) => Promise<void>}      — remover lead
 *   onAfterChange     {() => void}                   — recarregar pipeline na page
 */
export default function LeadDetailDrawer({
  lead: initialLead,
  stages = [],
  stageLabels = {},
  members = [],
  membersLoading = false,
  membersError = false,
  onClose,
  onEdit,
  onConvert,
  onDelete,
  onAfterChange,
}) {
  const [lead, setLead] = useState(initialLead);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(initialLead?.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostReasonError, setLostReasonError] = useState(null);
  const [pendingLost, setPendingLost] = useState(false);
  const [changingStage, setChangingStage] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const drawerRef = useRef(null);

  // Quando um ConfirmDialog está aberto sobre o drawer, ignoramos o listener
  // de ESC do drawer para evitar fechar tanto o confirm quanto o drawer pai.
  const isConfirmOpen = confirmDelete || confirmConvert || pendingLost;

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getLead(initialLead.id);
      setLead(res.data);
      setNotesDraft(res.data?.notes || "");
    } catch {
      toast.error("Não conseguimos carregar os detalhes do lead.");
    } finally {
      setLoading(false);
    }
  }, [initialLead.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Escape fecha o drawer — exceto quando um ConfirmDialog está aberto
  // (este já trata seu próprio ESC com stopPropagation, mas deixamos o guard
  // como defesa em profundidade — documentado: usamos a flag isConfirmOpen).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !isConfirmOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, isConfirmOpen]);

  // Foco inicial no drawer
  useEffect(() => {
    drawerRef.current?.focus();
  }, []);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setAddingNote(true);
      await addLeadActivity(lead.id, { type: "NOTE", content: newNote.trim() });
      toast.success("Nota adicionada.");
      setNewNote("");
      await reload();
      onAfterChange?.();
    } catch {
      toast.error("Erro ao adicionar nota.");
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await updateLead(lead.id, { notes: notesDraft });
      toast.success("Observações atualizadas.");
      setEditingNotes(false);
      await reload();
      onAfterChange?.();
    } catch {
      toast.error("Erro ao salvar observações.");
    } finally {
      setSavingNotes(false);
    }
  };

  /**
   * Atribui o lead a alguém da equipe.
   *
   * ⚠️ Convenção de id — `Lead.assignedTo` guarda o **`userId`** (id do `User`),
   * NÃO o `TenantMember.id`. O campo é um uuid solto no Prisma (sem relation/FK)
   * e semanticamente representa "qual usuário é responsável"; por isso casamos
   * `m.userId === lead.assignedTo` para resolver o nome de exibição.
   * Não confundir com `ChatSession.assignedToId` do MessageCenter, que aponta
   * para o `TenantMember.id` e **tem** FK — são campos diferentes.
   *
   * ⚠️ Contrato da API: `PATCH /api/leads/:id` valida com
   * `assignedTo: z.string().uuid().optional()` dentro de um `strictObject`.
   * A API aceita **uuid ou omissão**; `null` devolve 400 e omitir é no-op —
   * ou seja, não há como desatribuir hoje (ver `LeadAssigneeSelect`).
   *
   * O `LeadActivity` do tipo ASSIGNMENT é criado pelo próprio backend
   * (`leadService.updateLead`) — não registramos atividade aqui para não duplicar.
   */
  const handleAssigneeChange = async (userId) => {
    if (!userId || userId === lead?.assignedTo) return;
    try {
      setAssigning(true);
      await updateLead(lead.id, { assignedTo: userId });
      const member = members.find((m) => m?.userId === userId);
      toast.success(`Responsável definido: ${memberDisplayName(member)}.`);
      await reload();
      onAfterChange?.();
    } catch {
      toast.error("Não foi possível alterar o responsável.");
    } finally {
      setAssigning(false);
    }
  };

  const handleStepClick = async (toStageId) => {
    if (toStageId === lead.stage) return;
    if (toStageId === "LOST") {
      setPendingLost(true);
      return;
    }
    await changeStage(toStageId);
  };

  /**
   * Aplica a mudança de estágio. Retorna `true` em caso de sucesso e `false`
   * caso o backend rejeite (assim o caller pode manter o dialog aberto e
   * exibir erro inline em vez de fechar tudo).
   *
   * Para a transição LOST, propaga erros do tipo `details.fieldErrors.lostReason`
   * (Zod) como erro inline em vez de toast genérico — UX mais clara.
   */
  const changeStage = async (toStageId, extra = {}) => {
    try {
      setChangingStage(true);
      await updateLead(lead.id, { stage: toStageId, ...extra });
      const toLabel = stageLabels[toStageId] || toStageId;
      toast.success(`Estágio alterado para "${toLabel}".`);
      await reload();
      onAfterChange?.();
      return true;
    } catch (err) {
      // Para LOST: se o backend devolver 400 com erro de `lostReason`,
      // surface a mensagem do backend em toast (defesa em profundidade —
      // a UI já enforce required, mas o backend é a fonte da verdade).
      if (toStageId === "LOST") {
        const fieldMsg = extractZodFieldError(err, "lostReason");
        if (fieldMsg) {
          toast.error(fieldMsg);
          return false;
        }
      }
      toast.error("Não foi possível mover o lead.");
      return false;
    } finally {
      setChangingStage(false);
    }
  };

  /**
   * Confirma transição para LOST. O motivo agora é **obrigatório** —
   * backend exige `lostReason` quando `stage === "LOST"` (Zod superRefine).
   * A UI enforce o required:
   *  - Textarea marcado com `required` + `aria-required="true"` +
   *    `aria-invalid` quando há erro inline.
   *  - Interceptamos aqui ANTES de bater no backend: se motivo vazio,
   *    setamos `lostReasonError` e abortamos — sem fechar o dialog.
   *  - Defesa em profundidade: se mesmo assim o backend devolver 400 com
   *    `details.fieldErrors.lostReason`, propagamos via toast com a
   *    mensagem específica (ver `changeStage`).
   */
  const confirmLost = async () => {
    const reason = lostReason.trim();
    if (!reason) {
      setLostReasonError(
        "Conte o que aconteceu — é importante pra entender o funil.",
      );
      return;
    }
    setLostReasonError(null);
    const ok = await changeStage("LOST", { lostReason: reason });
    if (ok) {
      setPendingLost(false);
      setLostReason("");
    }
  };

  /**
   * Atualiza o textarea de motivo e limpa o erro inline conforme o user digita
   * (feedback otimista — não espera o user clicar de novo pra ver que ficou ok).
   */
  const handleLostReasonChange = (e) => {
    setLostReason(e.target.value);
    if (lostReasonError && e.target.value.trim()) {
      setLostReasonError(null);
    }
  };

  const handleConvertClick = () => setConfirmConvert(true);
  const handleConvertConfirm = async () => {
    setConfirmConvert(false);
    await onConvert?.(lead);
  };

  const handleDeleteClick = () => setConfirmDelete(true);
  const handleDeleteConfirm = async () => {
    setConfirmDelete(false);
    await onDelete?.(lead);
  };

  const activities = lead?.activities || [];
  const currentStageIdx = stages.findIndex((s) => s.id === lead?.stage);
  const isConverted = !!lead?.customerId || lead?.stage === "WON";
  const isLost = lead?.stage === "LOST";

  const initial = (lead?.name || "?").trim().charAt(0).toUpperCase();
  const avatarColor = stages.find((s) => s.id === lead?.stage)?.accent || "#3498db";

  return (
    <div className={styles.overlay} onClick={onClose} aria-hidden={false}>
      <aside
        ref={drawerRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes do lead ${lead?.name || ""}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <div
              className={styles.avatar}
              style={{ background: avatarColor }}
              aria-hidden="true"
            >
              {initial}
            </div>
            <div className={styles.headerInfo}>
              <h2 className={styles.name}>{lead?.name}</h2>
              <p className={styles.contact}>
                {lead?.phone && <span>📱 {lead.phone}</span>}
                {lead?.email && <span> · {lead.email}</span>}
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar painel"
          >
            ✕
          </button>
        </header>

        {/* AÇÕES PRINCIPAIS */}
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => onEdit?.(lead)}
          >
            ✏️ Editar
          </button>
          <button
            type="button"
            className={styles.btnConvert}
            onClick={handleConvertClick}
            disabled={isConverted || loading}
            title={isConverted ? "Este lead já virou cliente" : "Transformar em cliente"}
          >
            🏆 {isConverted ? "Já é cliente" : "Converter em cliente"}
          </button>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={handleDeleteClick}
            aria-label="Remover lead"
          >
            🗑️ Remover
          </button>
        </div>

        {/* STEPS DE ESTÁGIO */}
        <section className={styles.section} aria-label="Estágio atual">
          <h3 className={styles.sectionTitle}>Em que estágio está?</h3>
          <ol className={styles.steps}>
            {stages.map((s, idx) => {
              const active = idx === currentStageIdx;
              const completed = idx < currentStageIdx;
              return (
                <li key={s.id} className={styles.stepItem}>
                  <button
                    type="button"
                    className={`${styles.step} ${active ? styles.stepActive : ""} ${
                      completed ? styles.stepCompleted : ""
                    }`}
                    onClick={() => handleStepClick(s.id)}
                    disabled={changingStage || active}
                    aria-current={active ? "step" : undefined}
                    title={s.label}
                    style={
                      active
                        ? { background: s.accent, borderColor: s.accent, color: "#fff" }
                        : undefined
                    }
                  >
                    <span className={styles.stepIcon} aria-hidden="true">
                      {s.emoji}
                    </span>
                    <span className={styles.stepLabel}>{s.label}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>

        {/* RESPONSÁVEL */}
        <section className={styles.section} aria-label="Responsável pelo lead">
          <h3 className={styles.sectionTitle}>Quem é o responsável?</h3>
          <LeadAssigneeSelect
            id={`lead-assignee-${lead?.id ?? "none"}`}
            value={lead?.assignedTo || null}
            members={members}
            loading={membersLoading}
            error={membersError}
            busy={assigning}
            onChange={handleAssigneeChange}
          />
        </section>

        {/* MOTIVO DA PERDA (só quando LOST) */}
        {isLost && lead?.lostReason && (
          <section className={styles.section} aria-label="Motivo da perda">
            <h3 className={styles.sectionTitle}>Motivo da perda</h3>
            <p className={styles.lostReason}>{lead.lostReason}</p>
          </section>
        )}

        {/* OBSERVAÇÕES */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Observações</h3>
            {!editingNotes && (
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setEditingNotes(true)}
              >
                {lead?.notes ? "Editar" : "Adicionar"}
              </button>
            )}
          </div>
          {editingNotes ? (
            <>
              <textarea
                className={styles.textarea}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={3}
                aria-label="Observações do lead"
                placeholder="Anote aqui o que for útil sobre este lead…"
              />
              <div className={styles.inlineActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => {
                    setEditingNotes(false);
                    setNotesDraft(lead?.notes || "");
                  }}
                  disabled={savingNotes}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                >
                  {savingNotes ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className={`${styles.notesText} ${
                !lead?.notes ? styles.notesEmpty : ""
              }`}
              onClick={() => setEditingNotes(true)}
              aria-label={lead?.notes ? "Editar observações" : "Adicionar observações"}
            >
              {lead?.notes || "Sem observações. Clique para adicionar."}
            </button>
          )}
        </section>

        {/* HISTÓRICO */}
        <section className={styles.section} aria-label="Histórico de atividades">
          <h3 className={styles.sectionTitle}>Histórico</h3>
          {loading && <p className={styles.muted}>Carregando…</p>}
          {!loading && activities.length === 0 && (
            <p className={styles.muted}>
              Nenhuma atividade registrada ainda. Adicione uma nota abaixo.
            </p>
          )}
          {!loading && activities.length > 0 && (
            <div className={styles.activities}>
              {activities.map((a) => (
                <LeadActivityItem key={a.id} activity={a} stageLabels={stageLabels} />
              ))}
            </div>
          )}
        </section>

        {/* CAMPO DE NOVA NOTA (sticky no footer) */}
        <footer className={styles.footer}>
          <label htmlFor="lead-new-note" className={styles.footerLabel}>
            Adicionar nota
          </label>
          <textarea
            id="lead-new-note"
            className={styles.textarea}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Escreva uma anotação rápida sobre este lead…"
            rows={2}
          />
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleAddNote}
            disabled={addingNote || !newNote.trim()}
          >
            {addingNote ? "Salvando…" : "Salvar nota"}
          </button>
        </footer>

        {/* DIÁLOGOS DE CONFIRMAÇÃO (componente compartilhado) */}
        <ConfirmDialog
          isOpen={confirmConvert}
          title="Transformar em cliente?"
          message={`Vamos cadastrar "${lead?.name}" na sua lista de clientes. Você poderá agendar serviços para ele(a) imediatamente.`}
          confirmLabel="Sim, transformar em cliente"
          cancelLabel="Cancelar"
          confirmVariant="primary"
          onConfirm={handleConvertConfirm}
          onCancel={() => setConfirmConvert(false)}
        />

        <ConfirmDialog
          isOpen={confirmDelete}
          title="Remover este lead?"
          message={`Esta ação não pode ser desfeita. Tem certeza de que quer remover "${lead?.name}" do seu funil?`}
          confirmLabel="Sim, remover"
          cancelLabel="Cancelar"
          confirmVariant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDelete(false)}
        />

        <ConfirmDialog
          isOpen={pendingLost}
          title="Por que não fechou?"
          message="Conte o que aconteceu — é importante pra entender o funil."
          confirmLabel="Marcar como perdido"
          cancelLabel="Cancelar"
          confirmVariant="primary"
          confirmDisabled={pendingLost && lostReason.trim().length === 0}
          onConfirm={confirmLost}
          onCancel={() => {
            setPendingLost(false);
            setLostReason("");
            setLostReasonError(null);
          }}
        >
          {lostReasonError && (
            <p
              id="lost-reason-error"
              role="alert"
              className={styles.inlineError}
            >
              {lostReasonError}
            </p>
          )}
          <textarea
            className={styles.textarea}
            value={lostReason}
            onChange={handleLostReasonChange}
            placeholder="Ex.: preço acima do esperado, escolheu concorrente, etc."
            rows={3}
            aria-label="Motivo da perda"
            aria-required="true"
            aria-invalid={lostReasonError ? "true" : "false"}
            aria-describedby={lostReasonError ? "lost-reason-error" : undefined}
            required
          />
        </ConfirmDialog>
      </aside>
    </div>
  );
}
